import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogCategory } from "@/app/types/BlogCategory";
import { BlogSlugs, PublicPaths } from "@/app/vars/Routes";

export const realtimeAnalysisArticle: BlogArticle = {
  key: BlogArticleKey.REALTIME_ANALYSIS,
  slug: BlogSlugs.REALTIME_ANALYSIS,
  categoryId: BlogCategory.CALL_ANALYSIS,
  date: "2026-09-02",
  updated: "2026-09-29",
  readTime: 7,
  locales: {
    fr: {
      title: "Analyser ses appels en temps réel ou après coup ?",
      excerpt:
        "Le temps réel souffle pendant l'appel, l'analyse après coup fait ressortir ce qui revient. Les deux ne résolvent pas le même problème, et un seul se cumule.",
      description:
        "Le temps réel souffle pendant l'appel, l'analyse après coup montre ce qui revient. Ce que chacun résout, et lequel vous sert vraiment.",
      ctaTitle: "Voyez ce qui revient dans vos appels",
      ctaText:
        "Rattachez vos échanges aux fiches clients, puis demandez à l'assistant ce qui revient : il compte les objections et les questions sur l'ensemble, passages à l'appui. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La question revient dès qu'on compare des outils d'analyse d'appels : est-ce qu'il faut quelque chose qui parle pendant l'appel, ou quelque chose qui travaille une fois qu'il est fini ?",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les deux existent, les deux fonctionnent, et ils ne résolvent pas le même problème. Choisir sur la fiche technique mène presque toujours au mauvais outil.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce que le temps réel résout vraiment",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un outil en temps réel écoute pendant que vous parlez et affiche des éléments à l'écran : une réponse à une objection qui vient d'être formulée, un rappel de tarif, une question à poser ensuite.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Sa valeur réelle est une valeur de formation. Il sert à quelqu'un qui ne connaît pas encore le produit, l'argumentaire ou les objections du marché : un commercial arrivé il y a trois semaines, un fondateur qui vend une offre nouvelle. Il compense un manque de réflexes au moment exact où ce manque coûte cher.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Le temps réel ne rend pas meilleur, il évite de se tromper pendant qu'on apprend.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Et ce qu'il coûte",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Il partage votre attention.** Lire une suggestion pendant qu'une personne parle, c'est cesser de l'écouter. Le prospect le sent, et c'est exactement ce qu'un bon appel ne doit pas produire.",
            "**Il suppose une intégration vivante.** Il faut que l'outil soit dans la visio, avec les autorisations qui vont avec, et que tout le monde l'accepte.",
            "**Il ne garde rien de cumulé.** Il vous aide sur cet appel-là. Le suivant repart de zéro, et rien ne dit ce qui revient d'un appel à l'autre.",
          ],
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce que l'analyse après coup résout",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une analyse post-appel ne vous aide pas sur l'appel qui vient de finir. Elle vous aide sur les vingt suivants, parce qu'elle est la seule à pouvoir comparer.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une objection entendue une fois est une anecdote. La même objection sur douze appels est un défaut : de votre offre, de votre discours, ou de la cible que vous appelez. Ce passage de l'anecdote au motif ne se fait pas pendant l'appel, par construction, puisqu'il faut plusieurs appels pour l'observer.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Ce que le temps réel ne peut pas faire",
            items: [
              "Dire ce qui revient sur trois mois",
              "Nourrir votre ICP avec les mots des clients",
              "Repérer l'objection que vous contournez à chaque fois",
              "Compter combien de clients soulèvent la même objection",
            ],
          },
          good: {
            title: "Ce que l'après-coup ne peut pas faire",
            items: [
              "Vous aider au moment où vous bloquez",
              "Rattraper un appel déjà mal parti",
              "Former quelqu'un dès sa première semaine",
              "Remplacer la préparation d'un appel",
            ],
          },
        },
        {
          type: ArticleBlockType.H2,
          text: "Comment choisir, concrètement",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Vous intégrez des commerciaux régulièrement.** Le temps réel a du sens : il raccourcit le temps où une nouvelle recrue coûte des affaires.",
            "**Vous êtes seul, ou deux ou trois.** Vous connaissez votre produit mieux que n'importe quelle suggestion. Ce qui vous manque n'est pas un souffleur, c'est de la mémoire.",
            "**Vous cherchez à corriger votre offre ou votre ciblage.** Seule l'analyse cumulée répond, parce que la réponse est dans la répétition, pas dans un appel.",
            "**Vous voulez savoir ce qui revient chez vos clients, et chez quel type de client.** Là encore, il faut la matière consolidée : les mots exacts, les objections, les déclencheurs.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les deux approches ne s'excluent d'ailleurs pas. Mais si vous n'en prenez qu'une, prenez celle qui se cumule : le temps réel s'évapore avec l'appel, l'analyse consolidée vaut plus chaque mois.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Meidly analyse après l'appel, pas pendant",
          text: "C'est un choix, pas une limite technique : ce qui sert à corriger un discours, un ciblage ou un produit se lit sur des dizaines d'échanges, jamais sur un seul. Chaque échange rejoint la fiche du client, ses passages clés sont étiquetés, et l'assistant compte ce qui revient sur l'ensemble en citant chaque passage.",
          linkLabel: "Découvrir l'assistant",
          linkPath: PublicPaths.HOME,
        },
      ],
      faq: [
        {
          question: "Le temps réel est-il plus performant que l'analyse après coup ?",
          answer:
            "Ni l'un ni l'autre, ils répondent à deux questions différentes. Le temps réel aide une personne qui manque encore de réflexes, sur l'appel en cours. L'analyse après coup fait apparaître ce qui se répète d'un appel à l'autre, ce qu'aucun outil ne peut voir pendant un appel isolé.",
        },
        {
          question: "Meidly propose-t-il de l'analyse en temps réel ?",
          answer:
            "Non. On analyse les échanges une fois terminés : les appels Google Meet enregistrés avec l'extension Chrome, ou les fichiers audio et vidéo importés. Chaque échange met à jour la fiche du client, sa synthèse, ses engagements et ses deals, et l'assistant répond sur l'ensemble en citant les passages.",
        },
        {
          question: "Peut-on utiliser les deux ?",
          answer:
            "Oui, ils ne se gênent pas : l'un accompagne pendant l'appel, l'autre travaille après. Si le budget n'en permet qu'un, la question à se poser est de savoir si votre problème est de bien mener un appel ou de comprendre ce qui revient dans tous.",
        },
        {
          question: "Un souffleur en direct se voit-il côté prospect ?",
          answer:
            "Les suggestions ne s'affichent que de votre côté, mais la coupure d'attention, elle, se perçoit. Lire pendant que quelqu'un parle s'entend dans les silences et les relances à côté de la plaque.",
        },
      ],
    },
    en: {
      title: "Real-time or post-call analysis: which do you need?",
      excerpt:
        "Real-time prompts you during the call, post-call analysis surfaces what keeps coming back. They solve different problems, and only one compounds.",
      description:
        "Real-time prompts you during the call, post-call analysis shows what keeps coming back. What each one solves, and which you need.",
      ctaTitle: "See what keeps coming back in your calls",
      ctaText:
        "Attach your exchanges to customer files, then ask the assistant what keeps coming back: it counts the objections and questions across the set, with the passages to back it. No commitment.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The question comes up the moment you start comparing call analysis tools: do you want something that talks during the call, or something that works once it is over?",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Both exist, both work, and they do not solve the same problem. Choosing on the feature list almost always leads to the wrong tool.",
        },
        {
          type: ArticleBlockType.H2,
          text: "What real-time actually solves",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A real-time tool listens while you speak and puts things on screen: an answer to the objection just raised, a pricing reminder, the question to ask next.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Its real value is a training value. It serves someone who does not yet know the product, the pitch or the market's objections: a rep who started three weeks ago, a founder selling a new offer. It compensates for missing reflexes at the exact moment that gap is expensive.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Real-time does not make you better, it keeps you from getting it wrong while you learn.",
        },
        {
          type: ArticleBlockType.H2,
          text: "And what it costs",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**It splits your attention.** Reading a suggestion while someone is talking means you stopped listening. The prospect feels it, and that is exactly what a good call must not produce.",
            "**It assumes a live integration.** The tool has to sit inside the meeting, with the permissions that go with it, and everyone has to accept it.",
            "**It keeps nothing cumulative.** It helps you on that call. The next one starts from zero, and nothing tells you what recurs from one call to another.",
          ],
        },
        {
          type: ArticleBlockType.H2,
          text: "What post-call analysis solves",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Post-call analysis does not help with the call that just ended. It helps with the next twenty, because it is the only one that can compare.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An objection heard once is an anecdote. The same objection across twelve calls is a defect: in your offer, in your pitch, or in who you are calling. Moving from anecdote to pattern cannot happen during a call, by construction, since it takes several calls to observe.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "What real-time cannot do",
            items: [
              "Tell you what recurs across three months",
              "Feed your ICP with your clients' own words",
              "Spot the objection you talk around every time",
              "Count how many customers raise the same objection",
            ],
          },
          good: {
            title: "What post-call cannot do",
            items: [
              "Help you at the moment you are stuck",
              "Rescue a call that already went wrong",
              "Train someone in their first week",
              "Replace preparing for a call",
            ],
          },
        },
        {
          type: ArticleBlockType.H2,
          text: "How to choose, concretely",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**You onboard reps regularly.** Real-time makes sense: it shortens the window where a new hire costs you deals.",
            "**You are alone, or two or three.** You know your product better than any suggestion. What you lack is not a prompter, it is memory.",
            "**You want to fix your offer or your targeting.** Only cumulative analysis answers that, because the answer is in the repetition, not in one call.",
            "**You want to know what keeps coming back from your customers, and from which kind of customer.** Again, you need the consolidated material: the exact words, the objections, the triggers.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The two approaches do not exclude each other. But if you only take one, take the one that compounds: real-time evaporates with the call, consolidated analysis is worth more every month.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Meidly analyses after the call, not during",
          text: "That is a choice, not a technical limit: what lets you fix a pitch, a target or a product is read across dozens of exchanges, never a single one. Every exchange joins the customer file, its key passages are tagged, and the assistant counts what recurs across the set, citing each passage.",
          linkLabel: "Discover the assistant",
          linkPath: PublicPaths.HOME,
        },
      ],
      faq: [
        {
          question: "Is real-time better than post-call analysis?",
          answer:
            "Neither, they answer two different questions. Real-time helps someone who still lacks reflexes, on the call in progress. Post-call analysis surfaces what repeats from one call to the next, which no tool can see inside a single call.",
        },
        {
          question: "Does Meidly offer real-time analysis?",
          answer:
            "No. We analyse exchanges once they are over: Google Meet calls recorded with the Chrome extension, or imported audio and video files. Each exchange updates the customer file, its synthesis, its commitments and its deals, and the assistant answers across the set, citing the passages.",
        },
        {
          question: "Can you use both?",
          answer:
            "Yes, they do not get in each other's way: one assists during the call, the other works afterwards. If the budget only allows one, the question is whether your problem is running a call well or understanding what recurs across all of them.",
        },
        {
          question: "Can the prospect tell a live prompter is running?",
          answer:
            "The suggestions only show on your side, but the break in attention does come across. Reading while someone speaks is audible in the pauses and in follow-up questions that land beside the point.",
        },
      ],
    },
  },
};
